import {
  Matrix,
  compose,
  identity,
  inverse,
  scale,
  translate,
} from 'transformation-matrix';

/**
 * Origin locations
 */
export enum Origin {
  // "X Y" to match SVG translate() arguments
  'Left Top' = 1,
  'Center Top' = 2,
  'Right Top' = 3,
  'Left Center' = 4,
  'Center Center' = 5,
  'Right Center' = 6,
  'Left Bottom' = 7,
  'Center Bottom' = 8,
  'Right Bottom' = 9,
}

/**
 * Actual units represented by the Dimensioned Image/Crop.
 * To support conversions.
 */
export enum Units {
  feet,
  meters,
}

/**
 * How to show detailed units on the screens.
 */
export enum DisplayUnits {
  feet,
  feet_inches,
  feet_inches_decimal,
  meters,
  none,
}

/**
 * The location and size of an Image or Crop relative to it's container's origin.
 */
export class Dimensions {
  static displayValue(units: DisplayUnits, x: number, short = false): string {
    let display = '',
      feet,
      inches;
    switch (units) {
      case DisplayUnits.meters:
        let str = x.toFixed(1);
        if (str == '0.0') {
          str = '0';
        }
        if (short) {
          display = str || '';
        } else {
          display = str + ' m' || '';
        }
        break;
      case DisplayUnits.feet:
        feet = Math.trunc(x);
        display = `${feet}'`;
        break;
      case DisplayUnits.feet_inches:
        feet = Math.trunc(x);
        inches = Math.trunc((x - feet) * 12);
        if (feet) {
          inches = Math.abs(inches);
        }
        display = [feet ? `${feet}'` : '', ` ${inches}"`].join('');
        break;
      case DisplayUnits.feet_inches_decimal:
        feet = Math.trunc(x * 10) / 10;
        display = `${feet}'`;
        break;
      case DisplayUnits.none:
        display = '';
    }
    return display;
  }

  static displayUnitNames(units: DisplayUnits): string {
    let display: string;
    switch (units) {
      case DisplayUnits.meters:
        display = 'Meters';
        break;
      case DisplayUnits.feet:
        display = 'Feet';
        break;
      case DisplayUnits.feet_inches:
        display = 'Feet and Inches';
        break;
      case DisplayUnits.feet_inches_decimal:
        display = 'Decimal Feet';
        break;
      case DisplayUnits.none:
        display = 'None';
    }
    return display;
  }

  static selectListImperialOptions(): { text: string; value: DisplayUnits }[] {
    return Object.values(DisplayUnits)
      .filter(
        (v) =>
          typeof v === 'number' &&
          v !== DisplayUnits.none &&
          v !== DisplayUnits.meters,
      )
      .map((v: DisplayUnits) => ({
        text: Dimensions.displayUnitNames(v),
        value: v,
      }));
  }
}

export function displayShortUnits(units: Units): string {
  return units === Units.feet ? 'ft' : 'm';
}

export function coordinateOriginTransform(
  origin: Origin,
  width: number,
  height: number,
): Matrix {
  switch (origin) {
    case Origin['Left Top']:
      return identity();
    case Origin['Center Top']:
      return compose(translate(width / 2, 0));
    case Origin['Right Top']:
      return compose(translate(width, 0), scale(-1, 1));

    case Origin['Left Center']:
      return compose(translate(0, height / 2), scale(1, -1));
    case Origin['Center Center']:
      return compose(translate(width / 2, height / 2), scale(1, -1));
    case Origin['Right Center']:
      return compose(translate(width, height / 2), scale(-1, -1));

    case Origin['Left Bottom']:
      return compose(translate(0, height), scale(1, -1));
    case Origin['Center Bottom']:
      return compose(translate(width / 2, height), scale(1, -1));
    case Origin['Right Bottom']:
      return compose(translate(width, height), scale(-1, -1));
    default:
      // unusable areas default their origin to null - fall back to Left Top
      return identity();
  }
}

/**
 * Returns the transform to apply to the <rect> element that is the background
 * for Places and used to capture mouse/touch events.
 * It needs to be inverted because it is a child of the <g> element that has
 * the Place coordinate system transform applied to it.
 *
 * Used to generate display coordinates for points when a Map's origin
 * is not the default (top, left).
 * @export
 * @param {Origin} origin
 * @param {number} width
 * @param {number} height
 * @returns {string}
 */
export function inverseOriginTransform(
  origin: Origin,
  width: number,
  height: number,
): Matrix {
  const parentTransform = coordinateOriginTransform(origin, width, height);
  return inverse(parentTransform);
}
